import { InsurancePolicyType, VendorComplianceStatus, ComplianceReviewStatus } from '../types';

/** API returns enums as camelCase strings (e.g. "nonCompliant"); normalize for comparisons. */
export const normalizeComplianceStatus = (
  status?: VendorComplianceStatus | string | number | null,
  label?: string | null
): VendorComplianceStatus | undefined => {
  if (status !== undefined && status !== null && status !== '') {
    if (typeof status === 'number') {
      if (status >= 1 && status <= 3) return status as VendorComplianceStatus;
    } else if (typeof status === 'string') {
      const key = status.toLowerCase().replace(/[-_\s]/g, '');
      if (key === 'compliant' || key === '1') return VendorComplianceStatus.Compliant;
      if (key === 'expiringsoon' || key === '2') return VendorComplianceStatus.ExpiringSoon;
      if (key === 'noncompliant' || key === '3') return VendorComplianceStatus.NonCompliant;
    } else if (typeof status === 'object') {
      return undefined;
    } else {
      return status as VendorComplianceStatus;
    }
  }

  if (!label) return undefined;

  const normalizedLabel = label.toLowerCase().replace(/[-_\s]/g, '');
  if (normalizedLabel.includes('noncompliant')) {
    return VendorComplianceStatus.NonCompliant;
  }
  if (normalizedLabel.includes('expiring')) {
    return VendorComplianceStatus.ExpiringSoon;
  }
  if (normalizedLabel === 'compliant') {
    return VendorComplianceStatus.Compliant;
  }

  return undefined;
};

export const requiresComplianceAcknowledgment = (
  status?: VendorComplianceStatus | string | number | null,
  label?: string | null
): boolean => {
  const normalized = normalizeComplianceStatus(status, label);
  return normalized === VendorComplianceStatus.ExpiringSoon
    || normalized === VendorComplianceStatus.NonCompliant;
};

export const isNonCompliant = (
  status?: VendorComplianceStatus | string | number | null,
  label?: string | null
): boolean =>
  normalizeComplianceStatus(status, label) === VendorComplianceStatus.NonCompliant;

export const INSURANCE_POLICY_TYPE_OPTIONS = [
  { value: InsurancePolicyType.GeneralLiability, label: 'General Liability' },
  { value: InsurancePolicyType.Auto, label: 'Auto' },
  { value: InsurancePolicyType.Umbrella, label: 'Umbrella' },
  { value: InsurancePolicyType.WorkersCompensation, label: 'Workers Compensation' },
  { value: InsurancePolicyType.Property, label: 'Property' },
  { value: InsurancePolicyType.Other, label: 'Other' },
];

export const normalizeReviewStatus = (
  status?: ComplianceReviewStatus | string | number | null,
  label?: string | null
): ComplianceReviewStatus | undefined => {
  if (typeof status === 'number') {
    return status as ComplianceReviewStatus;
  }

  if (typeof status === 'string') {
    const key = status.toLowerCase().replace(/[-_\s]/g, '');
    if (key === 'notonfile' || key === '0') return ComplianceReviewStatus.NotOnFile;
    if (key === 'pendingreview' || key === '1') return ComplianceReviewStatus.PendingReview;
    if (key === 'accepted' || key === '2') return ComplianceReviewStatus.Accepted;
    if (key === 'rejected' || key === '3') return ComplianceReviewStatus.Rejected;
  }

  if (!label) return undefined;

  const normalizedLabel = label.toLowerCase();
  if (normalizedLabel.includes('pending')) return ComplianceReviewStatus.PendingReview;
  if (normalizedLabel.includes('accepted')) return ComplianceReviewStatus.Accepted;
  if (normalizedLabel.includes('rejected')) return ComplianceReviewStatus.Rejected;
  if (normalizedLabel.includes('not on file')) return ComplianceReviewStatus.NotOnFile;

  return undefined;
};

export const getReviewStatusClasses = (
  status?: ComplianceReviewStatus | string | number | null,
  label?: string | null
): string => {
  switch (normalizeReviewStatus(status, label)) {
    case ComplianceReviewStatus.Accepted:
      return 'bg-green-100 text-green-800';
    case ComplianceReviewStatus.PendingReview:
      return 'bg-yellow-100 text-yellow-800';
    case ComplianceReviewStatus.Rejected:
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export const getComplianceStatusClasses = (
  status?: VendorComplianceStatus | string | number | null,
  label?: string | null
): string => {
  switch (normalizeComplianceStatus(status, label)) {
    case VendorComplianceStatus.Compliant:
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    case VendorComplianceStatus.ExpiringSoon:
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
    case VendorComplianceStatus.NonCompliant:
      return 'bg-red-600 text-white dark:bg-red-700 dark:text-white ring-1 ring-red-700';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
};

export const getPolicyStatusClasses = (policyStatus: string): string => {
  switch (policyStatus) {
    case 'Valid':
      return 'bg-green-100 text-green-800';
    case 'Expiring Soon':
      return 'bg-yellow-100 text-yellow-800';
    case 'Expired':
    case 'Missing Expiration':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export const formatCurrency = (value?: number): string => {
  if (value === undefined || value === null) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
};

export const formatDate = (value?: string): string => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString();
};
